import React from "react";
import { graphql } from "gatsby";
import { DynamicHero } from "./DynamicHero";
import {
  StringValue,
  KontentComponentSettings,
  kontentImage,
  KontentVideo,
  KontentLinks,
  KontentMultiChoice,
  YesNo,
  KontentRichText,
  KontentLinkedVideo,
  KontentLinkedImage,
  KontentLinkedVectorArt,
  HeroStyles,
  HeroHeights,
  LeftRight,
} from "../../../types/index";
import { getMultiChoice } from "../../../lib/multichoice";
import { formatImages, formatVector } from "../../../lib/images";
import { formatLinks } from "../../../lib/links";
import { formatVideo } from "../../../lib/videos";
import { getComponentSettings } from "../../../lib/componentSettings";
import { MediaDynamicHero } from "./MediaDynamicHero";

export interface DynamicHeroContainerProps extends KontentComponentSettings {
  title: StringValue;
  text: KontentRichText;
  media?: KontentLinkedImage | KontentLinkedVideo;
  vector?: KontentLinkedVectorArt;
  link_configuration__link: KontentLinks;
  sidekick: KontentRichText;
  hide_sidekick_on_mobile: KontentMultiChoice<YesNo>;
  style: KontentMultiChoice<HeroStyles>;
  height: KontentMultiChoice<HeroHeights>;
  gradient_placement: KontentMultiChoice<LeftRight>;
}

const DynamicHeroContainer: React.FC<DynamicHeroContainerProps> = ({
  title,
  text,
  media,
  vector,
  link_configuration__link,
  sidekick,
  hide_sidekick_on_mobile,
  style,
  height,
  gradient_placement,
  ...props
}) => {
  const componentSettings = getComponentSettings({ ...props });
  const images =
    media?.value[0]?.system?.type === "image"
      ? formatImages(media?.value[0]?.elements as kontentImage["elements"])
      : undefined;
  const video =
    media?.value[0]?.system?.type === "video"
      ? formatVideo(media?.value[0]?.elements as KontentVideo["elements"])
      : undefined;
  const vectorItem = vector?.value[0]
    ? formatVector(vector.value[0].elements)
    : undefined;
  const cta =
    link_configuration__link &&
    formatLinks(link_configuration__link?.modular_content);
  const DynamicHeroStyle = getMultiChoice(style) || "dark_background";

  const overlayStyle =
    getMultiChoice(gradient_placement) === "left" ? "gradientLeft" : "gradient";

  if (
    DynamicHeroStyle === "media_dark_text" ||
    DynamicHeroStyle === "media_light_text"
  ) {
    return (
      <MediaDynamicHero
        desktopImage={images?.largeImage}
        mobileImage={images?.smallImage}
        videoUrl={video?.videoUrl}
        title={title?.value}
        text={text?.value !== "<p><br></p>" ? text : undefined}
        cta={cta}
        style={DynamicHeroStyle}
        sidekick={sidekick?.value !== "<p><br></p>" ? sidekick : undefined}
        height={getMultiChoice(height)}
        overlayStyle={overlayStyle}
        vector={vectorItem}
        hideSidekickOnMobile={getMultiChoice(hide_sidekick_on_mobile)}
        {...componentSettings}
      />
    );
  }

  return (
    <DynamicHero
      title={title?.value}
      text={text?.value !== "<p><br></p>" ? text : undefined}
      cta={cta}
      style={DynamicHeroStyle}
      height={getMultiChoice(height)}
      vector={vectorItem}
      sidekick={sidekick?.value !== "<p><br></p>" ? sidekick : undefined}
      hideSidekickOnMobile={getMultiChoice(hide_sidekick_on_mobile)}
      {...componentSettings}
    />
  );
};
export default DynamicHeroContainer;

export const fragmentDynamicHeroContainer = graphql`
  fragment DynamicHero on kontent_item_dynamic_hero {
    elements {
      component_settings__anchor_name {
        value
      }
      component_settings__hide_on_mobile {
        value {
          codename
        }
      }
      height {
        value {
          codename
        }
      }
      hide_sidekick_on_mobile {
        value {
          codename
        }
      }
      link_configuration__link {
        modular_content {
          ...kontentRichTextLink
        }
      }
      media {
        value {
          ...kontentImage
          ...kontentVideo
        }
      }
      sidekick {
        ...kontentModularRichText
      }
      style {
        value {
          codename
        }
      }
      text {
        ...kontentSimpleRichText
      }
      title {
        value
      }
      gradient_placement {
        value {
          codename
        }
      }
      vector {
        value {
          ...kontentVectorArt
        }
      }
    }
  }
`;
